import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@/hooks/useTheme';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const STORAGE_KEY = 'pwa-install-prompt-seen-v1';

function isStandaloneMode() {
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone;
  return window.matchMedia('(display-mode: standalone)').matches || Boolean(iosStandalone);
}

function isIOSDevice() {
  const ua = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(ua);
}

export default function InstallPrompt() {
  const colors = useTheme();
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = useState(false);

  const alreadySeen = useMemo(() => localStorage.getItem(STORAGE_KEY) === 'true', []);

  useEffect(() => {
    if (alreadySeen || isStandaloneMode()) return;
    const ios = isIOSDevice();
    setIsIOS(ios);

    if (ios) {
      setVisible(true);
      return;
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
      setVisible(true);
    };

    const handleAppInstalled = () => {
      localStorage.setItem(STORAGE_KEY, 'true');
      setVisible(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [alreadySeen]);

  const closePrompt = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setVisible(false);
  };

  const installApp = async () => {
    if (!deferredPrompt) {
      closePrompt();
      return;
    }

    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === 'accepted' || choice.outcome === 'dismissed') {
      localStorage.setItem(STORAGE_KEY, 'true');
    }

    setDeferredPrompt(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div style={styles.overlay}>
      <div style={{ ...styles.card, backgroundColor: colors.surface, borderColor: colors.border }}>
        <h3 style={{ ...styles.title, color: colors.text }}>Installer Tifosi sur votre mobile ?</h3>
        <p style={{ ...styles.message, color: colors.textSecondary }}>
          {isIOS
            ? 'Sur iPhone, appuyez sur Partager puis Sur l’ecran d’accueil pour installer Tifosi.'
            : 'Vous pourrez l’ouvrir comme une application, plus rapidement, depuis l’ecran d’accueil.'}
        </p>
        <div style={styles.actions}>
          <button
            type="button"
            onClick={closePrompt}
            style={{ ...styles.secondaryButton, borderColor: colors.border, color: colors.text }}
          >
            {isIOS ? 'Compris' : 'Plus tard'}
          </button>
          {!isIOS && (
            <button type="button" onClick={installApp} style={styles.primaryButton}>
              Installer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    zIndex: 2000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
  },
  card: {
    width: '100%',
    maxWidth: '420px',
    borderRadius: '16px',
    border: '1px solid',
    padding: '18px',
    boxShadow: '0 12px 30px rgba(0,0,0,0.25)',
  },
  title: {
    fontSize: '18px',
    marginBottom: '8px',
    lineHeight: 1.3,
  },
  message: {
    fontSize: '14px',
    lineHeight: 1.5,
    marginBottom: '16px',
  },
  actions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
  },
  secondaryButton: {
    border: '1px solid',
    borderRadius: '10px',
    padding: '10px 14px',
    fontWeight: 600,
    backgroundColor: 'transparent',
  },
  primaryButton: {
    borderRadius: '10px',
    padding: '10px 14px',
    fontWeight: 700,
    color: '#fff',
    backgroundColor: '#ea2a33',
  },
};
